import React, { useId } from "react";
import { Switch } from "../../primitives/Switch";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  ToggleColorVariant,
  ToggleInputProps,
  ToggleInputPropsWithFormControl,
  TogglePosition
} from "./ToggleInput.types";

const colorVariantStyles: Record<
  ToggleColorVariant,
  { label: string; description: string }
> = {
  default: {
    label: "",
    description: "text-content"
  },
  destructive: {
    label: "text-red-900",
    description: "text-red-800"
  }
};

const ToggleInputInput = React.forwardRef<
  React.ComponentRef<typeof Switch>,
  ToggleInputProps
>((props, ref) => {
  const {
    checked,
    onCheckedChange,
    disableOutline,
    togglePosition,
    colorVariant = "default",
    verticalCenter,
    ...rest
  } = props;

  return (
    <Switch
      {...rest}
      checked={checked}
      onCheckedChange={onCheckedChange}
      color={colorVariant}
      ref={ref}
    />
  );
});

ToggleInputInput.displayName = "ToggleInputInput";

/**
 * A labelled on / off switch for settings, with description and three layouts; the label
 * toggles it. Inside react-hook-form use `HookFormToggleInput`.
 */
export const ToggleInput = React.forwardRef<
  React.ComponentRef<typeof Switch>,
  ToggleInputPropsWithFormControl
>((props, ref) => {
  const {
    togglePosition = "bottom",
    colorVariant = "default",
    verticalCenter = false,
    ...restProps
  } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onCheckedChange, disabled } = inputProps;
  const variantStyles = colorVariantStyles[colorVariant];
  // One id shared by the switch and its label: the label then names the switch and toggles it
  // natively, with no extra click handling
  const generatedId = useId();
  const toggleId = restWrapperProps.id ?? generatedId;

  // The description is not a label (it would lengthen the name); clicking it still toggles
  const handleDescriptionClick = () => {
    if (!disabled && onCheckedChange && checked !== undefined) {
      onCheckedChange(!checked);
    }
  };

  // For left/right positions, we need a custom layout
  if (togglePosition === "left" || togglePosition === "right") {
    const toggle = (
      <ToggleInputInput
        ref={ref}
        {...inputProps}
        id={toggleId}
        togglePosition={togglePosition}
        colorVariant={colorVariant}
      />
    );

    return (
      <div className="w-full">
        <div
          className={cn(
            "flex justify-between gap-4",
            verticalCenter ? "items-center" : "items-start"
          )}
        >
          {togglePosition === "left" && (
            <div
              className={cn(
                "flex shrink-0 items-center",
                !verticalCenter && "pt-0.5"
              )}
            >
              {toggle}
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            {label && (
              <FormControlLabel
                htmlFor={toggleId}
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
                className={cn(
                  variantStyles.label,
                  !disabled && "cursor-pointer"
                )}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p
                className={cn(
                  "text-xs mt-1",
                  variantStyles.description,
                  !disabled && onCheckedChange && "cursor-pointer"
                )}
                onClick={handleDescriptionClick}
              >
                {description}
              </p>
            )}
          </div>
          {togglePosition === "right" && (
            <div
              className={cn(
                "flex w-12 shrink-0 items-center justify-end",
                !verticalCenter && "pt-0.5"
              )}
            >
              {toggle}
            </div>
          )}
        </div>
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      id={toggleId}
      label={
        label ? (
          <span
            className={cn(
              "w-full",
              !disabled && "cursor-pointer",
              variantStyles.label
            )}
          >
            {label}
          </span>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(
              !disabled && onCheckedChange && "cursor-pointer",
              variantStyles.description
            )}
            onClick={handleDescriptionClick}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
    >
      <ToggleInputInput
        ref={ref}
        {...inputProps}
        togglePosition={togglePosition}
        colorVariant={colorVariant}
        id={toggleId}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

ToggleInput.displayName = "ToggleInput";
